import { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';
import ReadinessGaugeContext from '../../components/readiness_gauge/questionnaire/ReadinessGaugeParametersMenu';
import { Box, Grid, TextField, Typography } from '@mui/material';
import {
  EnterpriseClassification,
  KPI,
  KPIGroup,
  Question,
  Questionnaire,
  ReadinessGaugeInstance,
  Resource,
  Response,
  User
} from '@esg/esg-global-types';
import { UserContext } from '../../context/UserContext';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import { QuestionnaireExtended, getQuestionnaires } from '../../lib/readiness_gauge/questionnaire';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import KPIPage from '../../components/readiness_gauge/questionnaire/KPIPage';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ReactComponent as SaveIcon } from '../../assets/icons/Save.svg';
import {
  ReadinessGaugeExtended,
  ReadinessGaugeParameters,
  setReadinessGaugeInstance
} from '../../lib/readiness_gauge/readiness_gauge_instance';
import moment from 'moment';
import {
  useLocation,
  Location,
  useNavigate,
  NavigateFunction,
  useBlocker,
  BlockerFunction,
  Blocker
} from 'react-router-dom';
import { DocumentReference } from 'firebase/firestore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ResetConfirmModal from '../../components/readiness_gauge/questionnaire/ResetConfirmModal';
import ProgressBar from '../../components/shared/ProgressBar';
import { ReactComponent as CheckIcon } from '../../assets/icons/Correct.svg';
import UnsavedChangesModal from '../../components/shared/modal/UnsavedChangesModal';
import KPISelect from '../../components/shared/input/select/KPISelect';
import { KPIExtended } from '../../lib/readiness_gauge/kpi';
import ConfirmationModal from '../../components/shared/modal/ConfirmationModal';
import GhostButton from '../../components/shared/button/GhostButton';
import PrimaryButton from '../../components/shared/button/PrimaryButton';
import PrimarySkeleton from '../../components/shared/loading/PrimarySkeleton';
import { getResources } from '../../graphql/services/resource.service';
import { GroupContext } from '../../context/GroupContext';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';
import { CompanyContext } from '../../context/CompanyContext';

interface QuestionnaireReadinessGauge extends Omit<
  ReadinessGaugeExtended,
  'user' | 'master_list_questionnaire' | 'responses'
> {
  user: User | DocumentReference | null;
  master_list_questionnaire: Questionnaire | DocumentReference | null;
  responses: Array<Response>;
}

const default_readiness_gauge_instance: QuestionnaireReadinessGauge = {
  id: '',
  name: '',
  completed: null,
  deleted: null,
  modified: null,
  created: new Date(),
  company: null,
  country: null,
  user: null,
  master_list_questionnaire: null,
  result: {},
  responses: []
};

/**
 * View for handling Readiness Gauge Questionnaire capturing.
 * @returns {JSX.Element}
 */
const ReadinessGaugeQuestionnaire = () => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  if (!group || !company) return null;

  // Track module duration.
  useModuleDurationTracking(
    'Sustainability',
    'Readiness Gauge Questionnaire',
    group.id,
    company.id
  );

  const [readinessGauge, setReadinessGauge] = useState<QuestionnaireReadinessGauge>(
    default_readiness_gauge_instance
  );
  const [readinessGaugeParameters, setReadinessGaugeParameters] =
    useState<ReadinessGaugeParameters>({
      company: null,
      country: null
    });
  const [parametersQuestionnaireName, setParametersQuestionnaireName] = useState<boolean>(true);
  const [questionnaireName, setQuestionnaireName] = useState<string | null>(null);
  const [questionnaireStarted, setQuestionnaireStarted] = useState<boolean>(false);
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireExtended | null>(null);
  const [currentKpiOrder, setCurrentKpiOrder] = useState<number>(0);
  const [questionnaireLoading, setQuestionnaireLoading] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(true);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [openResetModal, setOpenResetModal] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [finishConfirmModalOpen, setFinishConfirmModalOpen] = useState<boolean>(false);
  const [showGRIRefs, setShowGRIRefs] = useState<boolean>(false);
  const [showSDGs, setShowSDGs] = useState<boolean>(false);
  const [griResources, setGRIResources] = useState<Array<Resource>>([]);
  const [sdgResources, setSDGResources] = useState<Array<Resource>>([]);

  const location: Location = useLocation();

  const navigate: NavigateFunction = useNavigate();

  // Block navigating elsewhere when data has been entered into the input
  const should_block: BlockerFunction = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      return (
        isSaved !== true &&
        currentLocation.pathname !== nextLocation.pathname &&
        nextLocation.pathname !== '/sustainability/readiness/results'
      );
    },
    [isSaved]
  );

  const blocker: Blocker = useBlocker(should_block);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setConfirmModalOpen(true);
    }
  }, [blocker]);

  const handleProceedBlocker = () => {
    if (blocker.proceed) {
      blocker.proceed();
    }
  };

  const sorted_kpis: Array<KPI> = questionnaire?.kpis
    ? questionnaire.kpis
        .filter((kpi: KPI) => {
          const kpi_questions: Array<Question> = questionnaire.questions.filter(
            (question: Question) => question.kpi.id === kpi.id
          );
          return kpi_questions.length > 0;
        })
        .sort((a: KPI, b: KPI) => a.order - b.order)
    : [];

  const current_kpi_group: KPIGroup | null = questionnaire?.kpi_groups
    ? (questionnaire.kpi_groups.find(
        (kpi_group: KPIGroup) => kpi_group.id === sorted_kpis[currentKpiOrder].kpi_group.id
      ) ?? null)
    : null;

  const current_questions: Array<Question> = questionnaire?.questions
    ? questionnaire.questions.filter(
        (question: Question) => question.kpi.id === sorted_kpis[currentKpiOrder].id
      )
    : [];

  const responses_missing: boolean =
    readinessGauge.responses.length !== questionnaire?.questions.length;

  const handleStartQuestionnaire = async (): Promise<void> => {
    const gri_resources: Array<Resource> = await getResources({
      alias_substring_match: 'gri'
    });
    const sdg_resources: Array<Resource> = await getResources({
      alias_substring_match: 'sdg'
    });
    setGRIResources(gri_resources);
    setSDGResources(sdg_resources);
    setQuestionnaireStarted(true);
    setQuestionnaire(null);
    return;
  };

  const handleBackToQuestionnaires = (): void => {
    navigate('/sustainability/readiness');
  };

  const handlePreviousKpi = (): void => {
    setCurrentKpiOrder(Math.max(0, currentKpiOrder - 1));
  };

  const handleNextKpi = (): void => {
    setCurrentKpiOrder(currentKpiOrder + 1);
  };

  const handleToggleGRIRefs = (): void => {
    setShowGRIRefs(!showGRIRefs);
  };

  const handleToggleSDGs = (): void => {
    setShowSDGs(!showSDGs);
  };

  const handleReset = () => {
    setReadinessGauge({
      ...readinessGauge,
      responses: []
    });
    setCurrentKpiOrder(0);
    setIsSaved(false);
  };

  const handleResetClick = async (): Promise<void> => {
    setOpenResetModal(true);
  };

  const handleFinishClick = async (): Promise<void> => {
    setFinishConfirmModalOpen(true);
  };

  const handleCloseResetModal = (): void => {
    setOpenResetModal(false);
  };

  const handleUpdateResponses = (new_response: Response): void => {
    const other_question_responses: Array<Response> = readinessGauge.responses.filter(
      (response: Response) => {
        return response.question.id !== new_response.question.id;
      }
    );
    setReadinessGauge({
      ...readinessGauge,
      responses: [new_response, ...other_question_responses]
    });
    setIsSaved(false);
  };

  const fetchQuestionnaire = async (): Promise<void> => {
    try {
      if (group) {
        setQuestionnaireLoading(true);
        const questionnaire: Array<QuestionnaireExtended> | QuestionnaireExtended =
          await getQuestionnaires('readiness_gauge');
        if (!Array.isArray(questionnaire)) {
          setQuestionnaire({
            ...questionnaire,
            questions: questionnaire.questions.filter((question: Question) =>
              question.allowed_classifications.includes(
                (group?.enterprise_classification as EnterpriseClassification).name
              )
            )
          });
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ReadinessGaugeQuestionnaire failed on an unknown error while calling fetchQuestionnaire.',
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch readiness gauge. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setQuestionnaireLoading(false);
    }
  };

  const handleParametersChange = (parameters: ReadinessGaugeParameters) => {
    setReadinessGaugeParameters({
      ...readinessGaugeParameters,
      ...parameters
    });
    if (parametersQuestionnaireName) {
      const temp_questionnare_name = parameters.country
        ? `${parameters.country?.name} `
        : parameters.company
          ? `${parameters.company.name} `
          : group
            ? `${group.name} `
            : '';
      setQuestionnaireName(temp_questionnare_name.concat('Readiness Gauge'));
    }
  };

  const handleQuestionnaireNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setParametersQuestionnaireName(false);
    setQuestionnaireName(event.target.value);
    setIsSaved(false);
  };

  const handleSaveResponses = async (
    completed: boolean
  ): Promise<ReadinessGaugeInstance | undefined> => {
    try {
      setSaveLoading(true);
      if (user && questionnaire && group) {
        const new_readiness_gauge_instance: ReadinessGaugeInstance = {
          id: readinessGauge.id,
          created: readinessGauge.created,
          modified: moment(),
          name: questionnaireName ? questionnaireName : '',
          completed: completed ? moment() : null,
          deleted: readinessGauge.deleted,
          company: readinessGaugeParameters.company,
          country: readinessGaugeParameters.country,
          user: user,
          result: readinessGauge.result,
          master_list_questionnaire: questionnaire,
          responses: readinessGauge.responses
        };
        const saved_readiness_gauge_instance: ReadinessGaugeInstance =
          await setReadinessGaugeInstance(group.id, new_readiness_gauge_instance);

        setReadinessGauge({
          ...readinessGauge,
          id: saved_readiness_gauge_instance.id,
          responses: saved_readiness_gauge_instance.responses as Array<Response>
        });
        setIsSaved(true);
        return saved_readiness_gauge_instance;
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ReadinessGaugeQuestionnaire failed on an unknown error while calling handleSaveResponses.',
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to save readiness gauge. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleFinishQuestionnaire = async (): Promise<void> => {
    let completed_readiness_gauge: QuestionnaireReadinessGauge = readinessGauge;
    const saved_readiness_gauge: ReadinessGaugeInstance | undefined =
      await handleSaveResponses(true);
    setIsSaved(true);
    saved_readiness_gauge &&
      (completed_readiness_gauge = {
        ...completed_readiness_gauge,
        id: saved_readiness_gauge.id,
        name: saved_readiness_gauge.name
      });
    navigate('/sustainability/readiness/results', {
      state: {
        group: group,
        readinessGauge: completed_readiness_gauge
      }
    });
  };

  useEffect(() => {
    (async () => {
      if (location.state) {
        setReadinessGauge({
          ...location.state.readinessGauge
        });
        setReadinessGaugeParameters(location.state.readinessGaugeParameters);
        setQuestionnaireName(location.state.questionnaireName);
        await handleStartQuestionnaire();
      } else if (group) {
        setQuestionnaireName(`${group.name} Readiness Gauge`);
      }
    })();
  }, [group]);

  useEffect(() => {
    (async () => {
      if (questionnaireStarted) {
        await fetchQuestionnaire();
      }
    })();
  }, [questionnaireStarted]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isSaved) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSaved]);

  // Auto-scroll to top of questionnaire when changing sections.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentKpiOrder]);

  return !user ? (
    <LoadingScreen />
  ) : (
    <>
      <ConfirmationModal
        open={finishConfirmModalOpen}
        title={`Confirm Finish for ${questionnaireName}?`}
        description="Are you sure you would like to finish this Readiness Gauge Questionnaire? You will not be able to edit the responses once finished."
        confirm_loading={saveLoading}
        handleCloseModal={() => {
          setFinishConfirmModalOpen(false);
        }}
        handleConfirm={async () => await handleFinishQuestionnaire()}
        confirm_button_text="Finish"
      />
      <UnsavedChangesModal
        open={confirmModalOpen}
        save_loading={saveLoading}
        handleCloseConfirmModal={() => {
          setConfirmModalOpen(false);
        }}
        handleSaveChanges={async () => {
          await handleSaveResponses(false);
        }}
        handleProceed={handleProceedBlocker}
        unsaved_form_name={questionnaireName}
      />
      <ResetConfirmModal
        open={openResetModal}
        questionnaire_name={questionnaireName ? questionnaireName : ''}
        handleCloseResetModal={handleCloseResetModal}
        handleReset={handleReset}
      />
      <Grid container sx={{ px: 12 }}>
        <ReadinessGaugeContext
          questionnaire_started={questionnaireStarted}
          readiness_gauge_parameters={readinessGaugeParameters}
          handleParametersChange={handleParametersChange}
          handleStartQuestionnaire={handleStartQuestionnaire}
        />
      </Grid>
      <Grid container sx={{ px: 12, my: 4 }} spacing={0}>
        <Grid size={6}>
          <TextField
            value={questionnaireName}
            onInput={handleQuestionnaireNameChange}
            label="Questionnaire Name"
            variant="standard"
            fullWidth
          ></TextField>
        </Grid>
        {questionnaireStarted && (
          <>
            <Grid size={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ width: '70%' }}>
                {!questionnaireLoading && questionnaire ? (
                  <KPISelect
                    options={sorted_kpis}
                    selected_options={sorted_kpis[currentKpiOrder]}
                    input_label="Section"
                    handleChangeKPIs={(
                      kpi: KPI | KPIExtended | Array<KPI | KPIExtended> | null
                    ) => {
                      kpi &&
                        !Array.isArray(kpi) &&
                        setCurrentKpiOrder(
                          sorted_kpis.findIndex((sorted_kpi: KPI) => sorted_kpi.id === kpi.id)
                        );
                    }}
                    questions={questionnaire.questions}
                    responses={readinessGauge.responses}
                    searchable={false}
                    clearable={false}
                  />
                ) : (
                  <PrimarySkeleton variant="rounded" height={35} />
                )}
              </Box>
            </Grid>
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <KPIPage
                    loading={questionnaire === null}
                    kpi={sorted_kpis[currentKpiOrder]}
                    kpi_group_name={current_kpi_group?.name ?? ''}
                    questions={current_questions}
                    responses={readinessGauge.responses}
                    save_loading={saveLoading}
                    show_gri_refs={showGRIRefs}
                    show_sdgs={showSDGs}
                    gri_resources={griResources}
                    sdg_resources={sdgResources}
                    handleUpdateResponses={handleUpdateResponses}
                    handleToggleGRIRefs={handleToggleGRIRefs}
                    handleToggleSDGs={handleToggleSDGs}
                  />
                </Grid>
                <Grid size={12}>
                  <ProgressBar
                    current_step={currentKpiOrder + 1}
                    total_steps={sorted_kpis.length}
                  />
                </Grid>
                {currentKpiOrder === sorted_kpis.length - 1 && responses_missing && (
                  <Grid size={12} sx={{ textAlign: 'end' }}>
                    <Typography variant="caption" color="error">
                      Please fill out all responses for Readiness Gauge Sections
                    </Typography>
                  </Grid>
                )}
                <Grid size={7}>
                  <Box
                    mb={6}
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    gap={2}
                  >
                    <GhostButton
                      dark
                      onClick={handleBackToQuestionnaires}
                      startIcon={<KeyboardArrowLeftIcon />}
                    >
                      Questionnaires
                    </GhostButton>
                    <GhostButton
                      disabled={readinessGauge.responses.length === 0 || saveLoading}
                      dark
                      onClick={handleResetClick}
                      startIcon={<RestartAltIcon />}
                    >
                      Reset Responses
                    </GhostButton>
                    <PrimaryButton
                      disabled={isSaved}
                      loading={saveLoading}
                      onClick={async () => handleSaveResponses(false)}
                      startIcon={
                        <SaveIcon height={20} fill="none" stroke="currentColor" strokeWidth={4} />
                      }
                      sx={{ minWidth: 150 }}
                    >
                      Save
                    </PrimaryButton>
                    <Box>
                      <PrimaryButton
                        disabled={responses_missing}
                        onClick={handleFinishClick}
                        startIcon={<CheckIcon height={20} />}
                        sx={{ minWidth: 150, fill: 'none', stroke: 'currentcolor', strokeWidth: 6 }}
                      >
                        Finish
                      </PrimaryButton>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={5}>
                  <Box
                    mb={6}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                    gap={2}
                  >
                    <GhostButton
                      disabled={currentKpiOrder === 0}
                      dark
                      onClick={handlePreviousKpi}
                      startIcon={<KeyboardArrowLeftIcon />}
                    >
                      Previous
                    </GhostButton>
                    <Box>
                      <PrimaryButton
                        disabled={currentKpiOrder === sorted_kpis.length - 1}
                        onClick={handleNextKpi}
                        endIcon={<KeyboardArrowRightIcon />}
                      >
                        Next
                      </PrimaryButton>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default ReadinessGaugeQuestionnaire;
